import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileCheck, Lightbulb } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * SystemPromptModal — AI 應答指示(純條列需求)
 *
 *   PM 條列需求 → refine-agent-prompt 潤飾既有節點的 agentInstruction + few-shot
 *
 *   完整情境（文件 + API + 認證 + 補充）走另一個入口「API 串接」modal — 那邊處理 API 節點導入。
 */
export function SystemPromptModal({ open, onClose, onApply, isStreaming, avatarName, newMode = null }) {
    const { t } = useTranslation('wsModals');
    const [requirements, setRequirements] = useState('');
    const isSurvey = newMode === 'survey';

    useEffect(() => {
        if (!open) setRequirements('');
    }, [open]);

    if (!open) return null;

    const canApply = requirements.trim().length > 0;

    const handleApply = () => {
        if (isStreaming || !canApply) return;
        const text = requirements.trim();

        // Survey:寫 avatar.scenario(SurveyGeminiController._buildIdentitySection 讀這欄補進 system instruction)
        // 不要 few-shot 對話 — 問卷對話 flow 由 controller 控制,scenario 只放訪員 persona / 風格 / 禁區
        const prompt = isSurvey
            ? `請按 Google Gemini 提示詞工程規範（https://ai.google.dev/gemini-api/docs/prompting-strategies）把以下條列需求轉成這個問卷 AI 訪員 (${avatarName || 'avatar'}) 的訪員指示，並用 sync-ivr-config skill 寫回 avatar.scenario 欄位。

PM 需求清單：
${text}

要求：
1. 結構分段：訪員 persona / 應答風格 / 禁區與處理
2. 不要 few-shot 對話範例（問卷對話 flow 由 controller 控制，scenario 只放訪員角色描述）
3. 不要動 surveyQuestions / surveyFlow / firstMessage（這些有專門的編輯入口）
4. 只寫回 \`avatar.scenario\`（string），不要碰 ivrFlowConfig
5. 完成後用商家口吻摘要改了什麼（不要貼 JSON）`
            : `請用 refine-agent-prompt skill，按 Google Gemini 提示詞工程規範
（https://ai.google.dev/gemini-api/docs/prompting-strategies）把以下條列需求
轉成這個 AI 角色 (${avatarName || 'avatar'}) 的 agent instruction + few-shot 範例對話,
然後透過 sync-ivr-config skill 寫回。

PM 需求清單：
${text}

要求：
1. 每條需求都對應到 prompt 的某段（role / goal / instructions / constraints）
2. 至少 3 對 few-shot 範例對話（含 1 對展示禁區情境）
3. few-shot 所有對話格式一致
4. 在每個 AI 回應後括號註明對應哪幾條規則
5. 保留現有業務邏輯（情境、tool call、轉接條件）不動，只調文字風格 + 應答邏輯
6. 完成後用商家口吻摘要改了什麼（不要貼 JSON）`;

        onApply(prompt, { displayText: `${isSurvey ? t('prompt.display.titleSurvey') : t('prompt.display.title')}\n${text}` });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 inline-flex items-center gap-1.5">
                            <FileCheck size={11} /> system prompt
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('prompt.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">
                    <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 inline-flex items-start gap-2">
                        <Lightbulb size={14} strokeWidth={1.6} className="text-accent-700 shrink-0 mt-0.5" />
                        <div className="leading-relaxed">
                            {t('prompt.infoBody')}
                            <br />
                            <span className="text-ink-400 text-[11px]">{t('prompt.infoApiHint')}</span>
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">{t('prompt.reqListLabel')}</div>
                        <textarea
                            value={requirements}
                            onChange={(e) => setRequirements(e.target.value)}
                            rows={12}
                            placeholder={t('prompt.placeholder')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-sans leading-relaxed"
                            autoFocus
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isStreaming || !canApply}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                    >
                        {t('prompt.applyBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
}
